import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { profileTypes } from '../lib/constants';
import AppContainer from '../components/AppContainer';
// import useScrollToTop from '../hooks/useScrollToTop';
import { ROUTES } from '../lib/frontendRoutes';
import LoaderBlock from '../components/LoaderBlock';

if (navigator.onLine) {
    import('../components/OfflineBooksContainer'); // Preload when online
}

const NotFoundPage = lazy(() => import('../components/NotFoundPage'));
const AllBooksContainer = lazy(() => import('../components/AllBooksContainer'));
const HomeContainer = lazy(() => import('../components/HomeContainer'));
const BookContainer = lazy(() => import('../components/BookContainer'));

const RTRBookReaderContainer = navigator.onLine
    ? lazy(() => import('../components/RTRBookReaderContainer'))
    : require('../components/RTRBookReaderContainer').default;

const ProfileContainer = lazy(() => import('../components/ProfileContainer'));
const ReadingListContainer = lazy(() =>
    import('../components/ReadingListContainer')
);
const ManageReadingListContainer = lazy(() =>
    import('../components/ManageReadingList')
);
const MyReadingListDetailContainer = lazy(() =>
    import('../components/MyReadingListDetailContainer')
);
const CreateNewReadingList = lazy(() =>
    import('../components/CreateNewReadingList')
);
const ThemedReadingListsDetailContainer = lazy(() =>
    import('../components/ThemedReadingListsDetailContainer')
);
const AddBooksContainer = lazy(() => import('../components/AddBooksContainer'));
const VideoContainer = lazy(() => import('../components/VideoContainer'));
const VideoDetailContainer = lazy(() =>
    import('../components/VideoDetailContainer')
);
const ResourceContainer = lazy(() => import('../components/ResourceContainer'));
const ResourceDetailContainer = lazy(() =>
    import('../components/ResourceDetailContainer')
);
const PublicationFormContainer = lazy(() =>
    import('../components/PublicationFormContainer')
);
const BookUpload = lazy(() => import('../components/BookUpload'));
const BookPreview = lazy(() => import('../components/BookPreview'));
const BookPublication = lazy(() => import('../components/BookPublication'));
const ReadAloudsContainer = lazy(() =>
    import('../components/ReadAloudsContainer')
);
const ReadAloudsDetailContainer = lazy(() =>
    import('../components/ReadAloudsDetailContainer')
);
const MyFavouritesContainer = lazy(() =>
    import('../components/MyFavouritesContainer')
);
const DataDashboardContainer = lazy(() =>
    import ('../components/DataDashboardContainer')
);
const TermsAndConditionsContainer = lazy(() => 
    import ('../components/TermsAndConditionsContainer')
);
const OfflineBooksContainer = navigator.onLine
    ? lazy(() => import('../components/OfflineBooksContainer'))
    : require('../components/OfflineBooksContainer').default;

const RouteWithOfflineRedirect = ({ element }) => {
    return window.navigator.onLine ? (
        element
    ) : (
        <Navigate to="/offline" replace />
    );
};

const Router = () => {
    // useScrollToTop();
    return (
        <Suspense fallback={<LoaderBlock />}>
            <AppContainer>
                <Routes>
                    <Route
                        path={ROUTES.HOME}
                        element={
                            <RouteWithOfflineRedirect
                                element={<HomeContainer />}
                            />
                        }
                    />
                    <Route
                        path={ROUTES.BOOKS}
                        element={
                            <RouteWithOfflineRedirect
                                element={<AllBooksContainer />}
                            />
                        }
                    />
                    <Route
                        path={ROUTES.BOOK_DETAIL}
                        element={<BookContainer />}
                    />
                    <Route
                        path={ROUTES.READ_STORY_PAGE}
                        element={<RTRBookReaderContainer />}
                    />
                    <Route
                        path={ROUTES.READ_STORY}
                        element={<BookContainer />}
                    />
                    <Route
                        path={ROUTES.THEMED_READING_LIST}
                        element={<ThemedReadingListsDetailContainer />}
                    />
                    <Route
                        path={ROUTES.MANAGE_READING_LIST}
                        element={<NotFoundPage />}
                    />
                    <Route
                        path={ROUTES.RECOMMENDED_STORIES}
                        element={
                            <RouteWithOfflineRedirect
                                element={<AllBooksContainer recommendedBooks />}
                            />
                        }
                    />
                    <Route
                        path={ROUTES.TRENDING_STORIES}
                        element={
                            <RouteWithOfflineRedirect
                                element={<AllBooksContainer trendingBooks />}
                            />
                        }
                    />
                    <Route
                        path={ROUTES.GLOBAL_SEARCH}
                        element={
                            <RouteWithOfflineRedirect
                                element={
                                    <AllBooksContainer
                                        searchPage
                                        isSearchVariant
                                    />
                                }
                            />
                        }
                    />
                    <Route
                        path={ROUTES.CREDITS_PAGE}
                        element={
                            <RouteWithOfflineRedirect
                                element={
                                    <ProfileContainer
                                        profileType={profileTypes.USER}
                                    />
                                }
                            />
                        }
                    />
                    <Route
                        path={ROUTES.PUBLISHER_PAGE}
                        element={
                            <RouteWithOfflineRedirect
                                element={
                                    <ProfileContainer
                                        profileType={profileTypes.PUBLISHER}
                                    />
                                }
                            />
                        }
                    />
                    <Route
                        path={ROUTES.READING_LISTS}
                        element={
                            <RouteWithOfflineRedirect
                                element={<ReadingListContainer />}
                            />
                        }
                    />
                    <Route
                        path={ROUTES.MY_READING_LIST}
                        element={
                            <RouteWithOfflineRedirect
                                element={<ReadingListContainer />}
                            />
                        }
                    />
                    <Route
                        path={ROUTES.MY_READING_LIST_BOOK}
                        element={
                            <RouteWithOfflineRedirect
                                element={<ManageReadingListContainer />}
                            />
                        }
                    />
                    <Route
                        path={ROUTES.MY_READING_LIST_DETAIL}
                        element={
                            <RouteWithOfflineRedirect
                                element={<MyReadingListDetailContainer />}
                            />
                        }
                    />
                    <Route
                        path={ROUTES.CREATE_READING_LIST}
                        element={
                            <RouteWithOfflineRedirect
                                element={<CreateNewReadingList />}
                            />
                        }
                    />
                    <Route
                        path={ROUTES.READING_LIST}
                        element={
                            <RouteWithOfflineRedirect
                                element={<ThemedReadingListsDetailContainer />}
                            />
                        }
                    />
                    <Route
                        path={ROUTES.ADD_BOOKS_TO_READING_LIST}
                        element={
                            <RouteWithOfflineRedirect
                                element={<AddBooksContainer />}
                            />
                        }
                    />
                    <Route
                        path={ROUTES.VIDEOS}
                        element={
                            <RouteWithOfflineRedirect
                                element={<VideoContainer />}
                            />
                        }
                    />
                    <Route
                        path={ROUTES.VIDEO_DETAIL}
                        element={
                            <RouteWithOfflineRedirect
                                element={<VideoDetailContainer />}
                            />
                        }
                    />
                    <Route
                        path={ROUTES.RESOURCES}
                        element={
                            <RouteWithOfflineRedirect
                                element={<ResourceContainer />}
                            />
                        }
                    />
                    <Route
                        path={ROUTES.RESOURCE_DETAIL}
                        element={
                            <RouteWithOfflineRedirect
                                element={<ResourceDetailContainer />}
                            />
                        }
                    />
                    <Route
                        path={ROUTES.READ_ALOUDS}
                        element={<ReadAloudsContainer />}
                    />
                    <Route
                        path={ROUTES.READ_ALOUDS_DETAIL}
                        element={<ReadAloudsDetailContainer />}
                    />
                    <Route
                        path={ROUTES.MY_FAVOURITES}
                        element={
                            <RouteWithOfflineRedirect
                                element={<MyFavouritesContainer />}
                            />
                        }
                    />
                    <Route
                        path={ROUTES.OFFLINE_LIBRARY}
                        element={<OfflineBooksContainer />}
                    />
                    <Route
                        path={ROUTES.RESET_PASSWORD}
                        element={<Navigate to="/" />}
                    />
                    <Route
                        path={ROUTES.DATA_DASHBOARD}
                        element={<DataDashboardContainer />}
                    />
                    <Route
                        path={ROUTES.TERMS_AND_CONDITIONS}
                        element={<TermsAndConditionsContainer />}
                    />

                    {/* Conditional Routes for Desktop */}
                    {window.screen.width > 768 && (
                        <React.Fragment>
                            <Route
                                path={ROUTES.STORY_UPLOAD_PUBLICATION_FORM}
                                element={
                                    <PublicationFormContainer isStoryUpload />
                                }
                            />
                            <Route
                                path={ROUTES.EDIT_STORY_PUBLICATION_FORM}
                                element={
                                    <PublicationFormContainer isStoryEdit />
                                }
                            />
                            <Route
                                path={ROUTES.EDIT_STORY_UPLOAD_FORM}
                                element={<BookUpload isEdit />}
                            />
                            <Route
                                path={ROUTES.EDIT_STORY_PREVIEW}
                                element={<BookPreview />}
                            />
                            <Route
                                path={ROUTES.EDIT_STORY_PUBLICATION}
                                element={<BookPublication />}
                            />
                            <Route
                                path={ROUTES.NEW_EDITION}
                                element={
                                    <PublicationFormContainer isNewEdition />
                                }
                            />
                        </React.Fragment>
                    )}

                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
            </AppContainer>
        </Suspense>
    );
};

export default Router;
